import React, { type FunctionComponent, Fragment } from 'react'
import { useParams } from 'react-router'
import { css } from '@emotion/react'
import { spacing } from '@retailer-platform/shared-components'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { HeroBannerBreadcrumbs } from 'domains/merchandising/components/hero-banner-breadcrumbs/HeroBannerBreadcrumbs'
import { HeroBannerForm } from 'domains/merchandising/components/hero-banner-form/HeroBannerForm'
import { useQueryHeroBanner } from 'domains/merchandising/utils/useQueryHeroBanner.hooks'
import {
  type HeroBannerQueryParams,
  type HeroBannerFormData,
  type HeroBannerBannerType,
} from 'domains/merchandising/types/heroBanner.types'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { useUpdateHeroBanner } from 'domains/merchandising/api/hero-banners/updateHeroBanner.hooks'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { useRetailerId } from 'utils/routing/params.hooks'
import { mapFormDataToInput } from 'domains/merchandising/utils/heroBannerUtils'

const pageContainer = css({
  margin: `${spacing.X32} auto`,
  paddingLeft: spacing.X20,
  paddingRight: spacing.X20,
  maxWidth: '960px',
  width: '100%',
})

export const HeroBannerUpdate: FunctionComponent = () => {
  const { heroBannerId } = useParams<HeroBannerQueryParams>()
  const goToList = useGoToPath('merchandising-hero-banners-list')

  const {
    apiLoading: queryLoading,
    apiResult: queryResult,
    apiError: queryError,
  } = useQueryHeroBanner({ id: heroBannerId })
  const {
    updateHeroBanner,
    apiLoading: mutationLoading,
    apiError: mutationError,
    apiSuccess: mutationSuccess,
  } = useUpdateHeroBanner()

  const retailerId = useRetailerId()
  const handleFormSubmit = (formData: HeroBannerFormData) => {
    // TODO: Form validation
    updateHeroBanner({
      id: heroBannerId,
      bannerId: queryResult?.bannerId,
      retailerId,
      heroBannerData: mapFormDataToInput(formData),
    })
  }

  if (mutationSuccess) goToList()

  if (queryLoading) return <LoadingPage />

  // TODO: Redirect users back to list page?
  if (queryError) return <ErrorPage action={() => null} />

  const initialState = {
    ...queryResult,
  }

  return (
    <Fragment>
      <HeroBannerBreadcrumbs pageType="update" placementName={queryResult.name} />
      <div css={pageContainer}>
        <HeroBannerForm
          initialState={initialState}
          type={queryResult.type as HeroBannerBannerType}
          onFormSubmit={handleFormSubmit}
          onCancel={goToList}
          isSubmitting={mutationLoading}
          submitError={mutationError}
        />
      </div>
    </Fragment>
  )
}
